import { useCallback, useEffect, useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { Camera, X } from 'lucide-react'
import { AvatarCropDialog } from '../AvatarCropDialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { totemEditorIds } from '../../lib/totemPermissions'
import { GRADIENT_COLORS, SOLID_COLORS, type BorderType } from './borderStyles'
import { BorderSwatch } from './BorderSwatch'
import { TotemEditorsPicker } from './TotemEditorsPicker'
import type { Group, Totem, TotemBorderStyle } from '../../types'
import { useImagePickCrop } from '../../hooks/useImagePickCrop'

export interface TotemFormValues {
  emoji: string
  name: string
  borderStyle: TotemBorderStyle
  editorIds: string[]
  /** `null` removes the current photo; `undefined` leaves it alone. */
  imageBlob?: Blob | null
}

export function TotemEditDialog({
  open,
  onOpenChange,
  totem,
  group,
  currentUserId,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  totem: Totem | null
  group: Group
  currentUserId: string
  onSave: (values: TotemFormValues) => Promise<void>
}) {
  const [emoji, setEmoji] = useState('')
  const [name, setName] = useState('')
  const [borderType, setBorderType] = useState<BorderType>('rainbow')
  const [solidColor, setSolidColor] = useState(SOLID_COLORS[0])
  const [gradientColors, setGradientColors] = useState<[string, string]>([GRADIENT_COLORS[7], GRADIENT_COLORS[5]])
  const [editors, setEditors] = useState<Set<string>>(() => new Set([currentUserId]))
  const [saving, setSaving] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [pendingBlob, setPendingBlob] = useState<Blob | null | undefined>(undefined)
  useEffect(() => {
    if (!imagePreview?.startsWith('blob:')) return
    return () => URL.revokeObjectURL(imagePreview)
  }, [imagePreview])
  const { inputRef: fileInputRef, openPicker, onFileChange, cropSrc, closeCrop } = useImagePickCrop({ maxBytes: 5 * 1024 * 1024 })

  const resetForm = useCallback((t: Totem | null) => {
    setEmoji(t?.emoji ?? '')
    setName(t?.name ?? '')
    setImagePreview(t?.imageUrl ?? null)
    setPendingBlob(undefined)
    closeCrop()
    // Whoever is editing becomes an editor (their row is locked on), and
    // anyone who has since left the group drops out of the list.
    const members = new Set(group.memberIds)
    const kept = t ? totemEditorIds(t).filter(uid => members.has(uid)) : []
    setEditors(new Set([currentUserId, ...kept]))
    if (t) {
      setBorderType(t.borderStyle.type)
      if (t.borderStyle.type === 'solid') setSolidColor(t.borderStyle.color)
      if (t.borderStyle.type === 'gradient') setGradientColors(t.borderStyle.colors)
    } else {
      setBorderType('rainbow')
      setSolidColor(SOLID_COLORS[0])
      setGradientColors([GRADIENT_COLORS[7], GRADIENT_COLORS[5]])
    }
  }, [group.memberIds, currentUserId, closeCrop])

  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) resetForm(totem)
  }

  const toggleEditor = useCallback((uid: string) => {
    setEditors(prev => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  }, [])

  const currentStyle = useMemo<TotemBorderStyle>(() => {
    switch (borderType) {
      case 'rainbow':
        return { type: 'rainbow' }
      case 'solid':
        return { type: 'solid', color: solidColor }
      case 'gradient':
        return { type: 'gradient', colors: gradientColors }
    }
  }, [borderType, solidColor, gradientColors])

  const valid = emoji.trim().length > 0 && name.trim().length > 0

  function handleCropConfirm(blob: Blob) {
    setPendingBlob(blob)
    setImagePreview(URL.createObjectURL(blob))
    closeCrop()
    return Promise.resolve()
  }

  function handleRemoveImage() {
    setImagePreview(null)
    setPendingBlob(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!valid || saving) return
    setSaving(true)
    try {
      await onSave({
        emoji: emoji.trim(),
        name: name.trim(),
        borderStyle: currentStyle,
        editorIds: [...editors],
        imageBlob: pendingBlob,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {totem
              ? fbt('Edit totem', 'Title of the dialog editing a totem')
              : fbt('Add a totem', 'Title of the dialog creating a totem')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <BorderSwatch size="lg" style={currentStyle} emoji={emoji || '🎪'} imageUrl={imagePreview ?? undefined} />
              <button
                type="button"
                onClick={openPicker}
                className="absolute bottom-0 right-0 z-10 flex items-center justify-center size-8 rounded-full bg-primary text-primary-foreground ring-2 ring-background cursor-pointer transition-colors hover:bg-primary/90"
                aria-label={fbt('Upload totem photo', 'Accessible label for the totem photo upload button')}
              >
                <Camera className="size-3.5" />
              </button>
              {imagePreview && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-0 right-0 z-10 flex items-center justify-center size-6 rounded-full bg-destructive text-destructive-foreground ring-2 ring-background cursor-pointer"
                  aria-label={fbt('Remove totem photo', 'Accessible label for removing the totem photo')}
                >
                  <X className="size-3" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFileChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="totem-emoji">
                {fbt('Emoji', 'Label for the emoji field in the totem dialog')}
              </Label>
              <Input
                id="totem-emoji"
                value={emoji}
                onChange={e => setEmoji(e.target.value)}
                placeholder="🎪"
                className="mt-1"
                maxLength={4}
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="totem-name">
                {fbt('Name', 'Label for the name field in the totem dialog')}
              </Label>
              <Input
                id="totem-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={fbt(
                  'The pink flamingo',
                  'Placeholder example name for a group totem',
                )}
                className="mt-1"
                data-testid="totem-name-input"
              />
            </div>
          </div>

          <BorderStyleFields
            borderType={borderType}
            onBorderType={setBorderType}
            solidColor={solidColor}
            onSolidColor={setSolidColor}
            gradientColors={gradientColors}
            onGradientColors={setGradientColors}
          />

          {group.memberIds.length > 1 && (
            <TotemEditorsPicker
              group={group}
              currentUserId={currentUserId}
              selected={editors}
              onToggle={toggleEditor}
            />
          )}

          <Button type="submit" disabled={!valid || saving} data-testid="totem-save">
            {fbt('Save', 'Button saving the totem')}
          </Button>
        </form>
      </DialogContent>
      <AvatarCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={closeCrop}
        onConfirm={handleCropConfirm}
      />
    </Dialog>
  )
}

function BorderStyleFields({
  borderType,
  onBorderType,
  solidColor,
  onSolidColor,
  gradientColors,
  onGradientColors,
}: {
  borderType: BorderType
  onBorderType: (type: BorderType) => void
  solidColor: string
  onSolidColor: (color: string) => void
  gradientColors: [string, string]
  onGradientColors: (colors: [string, string]) => void
}) {
  return (
    <div>
      <Label>{fbt('Border style', 'Label for the border style picker in the totem dialog')}</Label>
      <div className="flex gap-2 mt-2">
        {(['rainbow', 'solid', 'gradient'] as const).map(type => (
          <button
            key={type}
            type="button"
            onClick={() => onBorderType(type)}
            className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
              borderType === type
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }`}
          >
            {type === 'rainbow'
              ? fbt('Rainbow', 'Border style option: rainbow')
              : type === 'solid'
                ? fbt('Solid', 'Border style option: solid color')
                : fbt('Gradient', 'Border style option: two-color gradient')}
          </button>
        ))}
      </div>

      {borderType === 'solid' && (
        <ColorGrid colors={SOLID_COLORS} selected={solidColor} onSelect={onSolidColor} />
      )}

      {borderType === 'gradient' && (
        <div className="mt-3 space-y-2">
          <div>
            <span className="text-xs text-muted-foreground">
              {fbt('Color 1', 'Label for the first gradient color')}
            </span>
            <ColorGrid
              colors={GRADIENT_COLORS}
              selected={gradientColors[0]}
              onSelect={c => onGradientColors([c, gradientColors[1]])}
            />
          </div>
          <div>
            <span className="text-xs text-muted-foreground">
              {fbt('Color 2', 'Label for the second gradient color')}
            </span>
            <ColorGrid
              colors={GRADIENT_COLORS}
              selected={gradientColors[1]}
              onSelect={c => onGradientColors([gradientColors[0], c])}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function ColorGrid({
  colors,
  selected,
  onSelect,
}: {
  colors: string[]
  selected: string
  onSelect: (color: string) => void
}) {
  return (
    <div className="grid grid-cols-6 gap-1.5 mt-2">
      {colors.map(color => (
        <button
          key={color}
          type="button"
          onClick={() => onSelect(color)}
          className={`size-8 rounded-full cursor-pointer transition-transform hover:scale-110 ${
            selected === color ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-110' : ''
          }`}
          style={{ backgroundColor: color }}
          aria-label={color}
        />
      ))}
    </div>
  )
}
